<svelte:options
  customElement={{
    tag: "oc-auth-field-v1",
    extend: window.__components.extend(),
    props: {
      variant: { type: "String" },
      passwordVisible: { type: "Boolean", attribute: "password-visible" },
      showCounter: { type: "Boolean", attribute: "show-counter" },
      withoutOptionalLabel: { type: "Boolean", attribute: "without-optional-label" },
    },
  }}
/>

<script lang="ts">
  import { toggle } from "@otto-ec/global-resources/toggle";
  import { onMount } from "svelte";
  import CounterV1 from "../../../common/components/CounterV1.svelte";
  import DefaultStyles from "../../../common/components/DefaultStyles/DefaultStyles.svelte";
  import { FormLabelIconV1 } from "../../../common/components/FormLabelIcon";
  import type { Props } from "./AuthFieldV1.types";

  let {
    variant = "default",
    passwordVisible = false,
    showCounter = false,
    withoutOptionalLabel = false,
  }: Props = $props();

  const Host = $host();

  let inputElement: HTMLInputElement | null = $state(Host.querySelector("input"));
  let labelElement: HTMLLabelElement | null = $state(Host.querySelector("label"));

  let hasOptionalLabel = toggle.get("oc_optional_label_on_inputfields", false);
  const OPTIONAL_SUFFIX = " (optional)";
  const OPTIONAL_SUFFIX_REGEX = /\s*\(optional\)/g;
  const originalLabelTexts = new WeakMap<HTMLElement, string>();

  let value = $state("");
  let type = $state("");
  let maxlength: number | undefined = $state();
  let minlength: number | undefined = $state();
  let isPasswordField = $state(false);
  let isRequired = $state(false);
  let mounted = $state(false);

  const updateOptionalLabel = (isFieldRequired: boolean, hideOptionalLabel: boolean): void => {
    const shouldShowOptional = !isFieldRequired && hasOptionalLabel && !hideOptionalLabel;

    Array.from(Host.children).forEach((node) => {
      if (!(node instanceof HTMLElement) || node.slot !== "label") return;

      const text = node.textContent;
      if (!text) return;

      const baseText = originalLabelTexts.get(node) ?? text.replace(OPTIONAL_SUFFIX_REGEX, "");
      originalLabelTexts.set(node, baseText);
      const newText = shouldShowOptional ? `${baseText}${OPTIONAL_SUFFIX}` : baseText;

      /*                   */
      /*                                                                                */
      /*                                                                               */
      if (newText !== text) node.textContent = newText;
    });
  };

  const syncValue = (): void => {
    value = inputElement?.value ?? "";
  };

  const syncType = (): void => {
    const currentType = inputElement?.type ?? "";
    if (type !== currentType) {
      /*                                                                                      */
      isPasswordField = inputElement?.type === "password";
    }
    type = currentType;
  };

  const syncMinLength = (): void => {
    minlength =
      inputElement?.minLength && inputElement.minLength >= 0 ? inputElement.minLength : undefined;
  };

  const syncMaxLength = (): void => {
    maxlength =
      inputElement?.maxLength && inputElement.maxLength >= 0 ? inputElement.maxLength : undefined;
  };

  const syncRequired = (): void => {
    isRequired = inputElement?.required || inputElement?.ariaRequired == "true";
  };

  $effect(() => {
    syncValue();
    syncType();
    syncMinLength();
    syncMaxLength();
    syncRequired();
  });

  $effect(() => {
    if (labelElement) labelElement.slot = "label";
  });

  $effect(() => {
    updateOptionalLabel(isRequired, withoutOptionalLabel);
  });

  onMount(() => {
    /*                                                               */
    /*                                                              */
    /*                                                  */
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        mounted = true;
      });
    });

    const observer = new MutationObserver((changes) => {
      if (inputElement !== Host.querySelector("input")) {
        inputElement = Host.querySelector("input");
      }

      if (labelElement !== Host.querySelector("label")) {
        labelElement = Host.querySelector("label");
      }

      changes
        .filter(
          (change) => change.type === "attributes" && change.target === (inputElement as Node),
        )
        .forEach((change) => {
          if (change.attributeName === "type") {
            syncType();
          } else if (change.attributeName === "minlength") {
            syncMinLength();
          } else if (change.attributeName === "maxlength") {
            syncMaxLength();
          }
        });
    });

    observer.observe(Host, {
      childList: true,
      attributes: true,
      subtree: true,
      attributeFilter: ["type", "minlength", "maxlength"],
    });

    return () => observer.disconnect();
  });

  $effect(() => {
    if (inputElement && isPasswordField) {
      type = passwordVisible ? "text" : "password";
      inputElement.type = type;
    }
  });

  const togglePasswordVisibility = (ev: Event): void => {
    passwordVisible = !passwordVisible;
    ev.stopPropagation();
    ev.preventDefault();
  };

  /*                                                                        */
  $effect(() => {
    /*                                                         */
    if (inputElement && !inputElement.placeholder) inputElement.placeholder = " ";
  });
</script>

<div
  class={`auth-field auth-field--${variant}`}
  class:auth-field--with-password={isPasswordField}
  class:mounted
  oninput={syncValue}
  data-oc-floating-focus-v1-masked
>
  <div class="label" data-oc-floating-focus-v1-mask-element>
    <slot
      name="label"
      onslotchange={() => {
        updateOptionalLabel(isRequired, withoutOptionalLabel);
      }}
    ></slot>
    <FormLabelIconV1 {variant} />
  </div>
  <slot></slot>
  {#if isPasswordField}
    <!-- svelte-ignore a11y_no_static_element_interactions -->
    <!-- svelte-ignore a11y_click_events_have_key_events -->
    <oc-link-v2
      class="auth-field__password-toggle"
      variant="secondary"
      size={/*                      */ "75"}
      as-button={true}
      onclick={togglePasswordVisibility}
      onfocusin={(ev) => ev.stopPropagation()}
      onfocusout={(ev) => ev.stopPropagation()}
      oc-aria-label={passwordVisible ? "Passwort ausblenden" : "Passwort anzeigen"}
      >{passwordVisible ? "Ausblenden" : "Anzeigen"}</oc-link-v2
    >
  {/if}
</div>

<div class="two-columns">
  <div>
    <slot name="error"></slot>
    <slot name="hint"></slot>
  </div>
  {#if showCounter}
    <CounterV1 counterValue={value} maxCounter={maxlength} minCounter={minlength} />
  {/if}
</div>

<DefaultStyles display="block" />

<style lang="scss">
  @use "@otto-ec/design-tokens/component" as tokens;
  @use "@otto-ec/otto-components-utils/scss/mixins";
  :global {
    @mixin transition($properties...) {
      @media (prefers-reduced-motion: no-preference) {
        transition-property: $properties;
        transition-duration: 0.1s;
        transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
      }
    }
    $gap: 8px;

    :host {
      --oc-floating-focus-v1-preserve-outline: "";
      --displayToggle: flex;

      /*                */
      --opacity: 1;
      --label-top: -0.5rem;
      --label-left: #{tokens.$oc-component-form-field-floating-label-spacing-left};
      --label-padding: 0 #{tokens.$oc-component-form-field-floating-label-inner-spacing-x};
      --label-font: #{tokens.$oc-component-form-field-floating-label-font};
      /*                                                                         */
      /*                                                                                     */
      --label-max-width: 100%;
    }

    .auth-field {
      position: relative;
      border-radius: tokens.$oc-component-form-field-border-radius;

      &:hover ::slotted(input) {
        outline-width: 2px;
      }

      &:focus-within {
        ::slotted(input) {
          outline-width: 2px;
        }

        &:not(.auth-field--error) ::slotted(input) {
          outline-color: tokens.$oc-component-form-default-border-color-focus;
        }
      }

      ::slotted(input) {
        all: unset;
        box-sizing: border-box;

        background: tokens.$oc-component-form-background-color;
        outline: 1px solid tokens.$oc-component-form-default-border-color;

        padding: tokens.$oc-component-form-field-spacing-y tokens.$oc-component-form-field-spacing-x;
        width: 100%;

        color: tokens.$oc-component-form-field-input-color;
        --oc-floating-focus-v1-full-bleed: "";

        &::placeholder {
          font: tokens.$oc-component-form-field-placeholder-font;
          color: tokens.$oc-component-form-field-placeholder-color;
          opacity: var(--opacity);
          white-space: nowrap;
          text-overflow: ellipsis;
          max-width: 100%;
        }
      }

      &.mounted ::slotted(input)::placeholder {
        @include transition(opacity);
      }

      ::slotted(label) {
        display: block;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        user-select: none;
        cursor: text;
      }

      .label {
        z-index: calc(
          var(--oc-floating-focus-v1-z-index) + 1
        ); /*                                           */
        position: absolute;
        top: var(--label-top);
        left: var(--label-left);
        padding: var(--label-padding);
        max-width: calc(100% - 2 * #{tokens.$oc-semantic-spacing-50});

        font: var(--label-font);
        background-color: tokens.$oc-component-form-background-color;
        border-radius: tokens.$oc-component-form-field-label-border-radius;
        color: tokens.$oc-component-form-default-label-color;

        /*                            */
        display: flex;
        align-items: center;
        gap: tokens.$oc-component-form-state-icon-gap-x;
        pointer-events: none; /*                                                                     */
      }

      &.mounted .label {
        @include transition(line-height, top, font-size);
      }

      /*                                       */
      &--with-password {
        $passwordToggleWidth: calc(#{tokens.$oc-component-form-field-spacing-x} + 4.25rem + $gap);

        ::slotted(input) {
          padding-right: $passwordToggleWidth;
        }

        .label {
          /*                                                                         */
          /*                                                                           */
          /*                                                                           */
          max-width: min(
            calc(100% - 2 * #{tokens.$oc-semantic-spacing-50}),
            var(--label-max-width)
          );
        }
      }

      &__password-toggle {
        position: absolute;
        display: var(--displayToggle);
        justify-content: right;
        align-self: center;

        top: 0;
        bottom: 0;
        right: 0;
        min-width: 90px;

        cursor: pointer;

        padding: tokens.$oc-component-form-field-spacing-y tokens.$oc-component-form-field-spacing-x;
      }

      &--error {
        ::slotted(input) {
          outline-color: tokens.$oc-component-form-error-border-color;
        }

        .label {
          color: tokens.$oc-component-form-error-label-color;
        }
      }
    }

    ::slotted([slot="error"]) {
      font: tokens.$oc-component-form-validation-message-font;
      color: tokens.$oc-component-form-error-validation-message-color;
      word-break: break-word;
    }

    ::slotted([slot="hint"]) {
      font: tokens.$oc-component-form-hint-font;
      color: tokens.$oc-component-form-default-hint-color;
      word-break: break-word;
    }

    .two-columns {
      display: grid;
      grid-column-gap: 16px;
      width: 100%;

      > .counter {
        margin-top: tokens.$oc-component-form-hint-gap-y;
        justify-self: end;
        grid-column-start: 2;
      }
    }
  }
</style>
